import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring, animate } from "framer-motion";
import { MessageCircle, Star, PawPrint, ArrowDown, Sparkles, Shield, Zap, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { localePath } from "@/i18n/localePath";
import heroDog from "@/assets/hero-dog-clean.jpg";

/* ─── Animated counter ─── */
const AnimatedCounter: React.FC<{ target: number; suffix?: string; delay?: number }> = ({ target, suffix = "", delay = 0 }) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const timeout = setTimeout(() => {
      const controls = animate(0, target, {
        duration: 2,
        ease: [0.25, 0.1, 0.25, 1],
        onUpdate: (v) => setValue(Math.floor(v)),
      });
      return () => controls.stop();
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, delay]);
  return <>{value.toLocaleString()}{suffix}</>;
};

/* ─── Floating chat bubble ─── */
const FloatingChatBubble: React.FC<{
  text: string; side: "left" | "right"; delay: number; x: string; y: string; rotate?: number;
}> = ({ text, side, delay, x, y, rotate = 0 }) => (
  <motion.div
    className={`absolute z-20 max-w-[180px] ${
      side === "right"
        ? "rounded-[18px] rounded-br-[4px] bg-primary text-primary-foreground"
        : "rounded-[18px] rounded-bl-[4px] bg-card/90 border border-border/50 backdrop-blur-md"
    } px-4 py-3 shadow-xl`}
    style={{ left: x, top: y, rotate }}
    initial={{ opacity: 0, scale: 0.5, y: 30 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ delay, duration: 0.7, type: "spring", stiffness: 200, damping: 15 }}
  >
    <p className={`text-xs leading-snug font-medium ${side === "left" ? "text-foreground" : ""}`}>{text}</p>
    <motion.div
      className="absolute -bottom-1.5 w-2.5 h-2.5 rounded-full bg-primary/30"
      style={{ [side === "right" ? "right" : "left"]: 8 }}
      animate={{ scale: [0.8, 1.3, 0.8], opacity: [0.4, 0.8, 0.4] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
  </motion.div>
);

/* ─── Main Hero ─── */
export const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lp = (path: string) => localePath(path, i18n.language);
  const sectionRef = useRef<HTMLElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const fadeOut = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      mouseX.set(((e.clientX / window.innerWidth) - 0.5) * 24);
      mouseY.set(((e.clientY / window.innerHeight) - 0.5) * 16);
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, [mouseX, mouseY]);

  const scrollToHowItWorks = () => {
    document.querySelector("#how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  const titleChars = "PETERNITY".split("");

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-[100dvh] flex items-center px-4 sm:px-6 overflow-hidden pt-16 pb-8"
    >
      {/* ─── Animated background mesh ─── */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-background" />
        {[
          { w: 700, h: 700, r: "-8%", t: "0%", color: "hsl(20 100% 65% / 0.12)", blur: 80, dur: 10 },
          { w: 500, h: 500, r: "60%", t: "50%", color: "hsl(35 80% 65% / 0.08)", blur: 100, dur: 14 },
          { w: 400, h: 400, r: "10%", t: "70%", color: "hsl(15 90% 60% / 0.07)", blur: 60, dur: 12 },
        ].map((orb, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{ width: orb.w, height: orb.h, right: orb.r, top: orb.t, background: orb.color, filter: `blur(${orb.blur}px)` }}
            animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: orb.dur, repeat: Infinity, ease: "easeInOut", delay: i * 2 }}
          />
        ))}
        {/* Noise texture */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Floating paw particles */}
      {Array.from({ length: 8 }).map((_, i) => (
        <motion.span
          key={i}
          className="absolute text-primary/10 pointer-events-none select-none"
          style={{ left: `${8 + Math.random() * 84}%`, top: `${8 + Math.random() * 84}%`, fontSize: 14 + Math.random() * 16 }}
          animate={{ y: [0, -25 - Math.random() * 30, 0], rotate: [0, 360], opacity: [0.06, 0.18, 0.06] }}
          transition={{ duration: 10 + Math.random() * 8, repeat: Infinity, delay: Math.random() * 5, ease: "easeInOut" }}
        >
          🐾
        </motion.span>
      ))}

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background via-background/80 to-transparent z-[2]" />

      {/* ─── Content ─── */}
      <motion.div
        className="max-w-6xl w-full mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-16 relative z-10"
        style={{ opacity: fadeOut }}
      >
        {/* Left: Text */}
        <motion.div className="flex-1 text-center lg:text-left" style={{ y: titleY }}>
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6 sm:mb-8 backdrop-blur-sm"
            style={{ boxShadow: "0 0 30px hsl(var(--primary) / 0.08)" }}
          >
            <motion.span animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
              <Sparkles size={14} className="text-primary" />
            </motion.span>
            <span className="text-xs font-bold text-primary tracking-wide">{t('hero.badge')}</span>
          </motion.div>

          {/* PETERNITY — character by character */}
          <motion.div style={{ x: smoothX, y: smoothY }}>
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
              className="text-[42px] sm:text-[58px] md:text-[70px] lg:text-[84px] font-[900] tracking-[4px] sm:tracking-[8px] leading-[0.95] text-foreground"
            >
              {titleChars.map((char, i) => (
                <motion.span
                  key={i}
                  className="inline-block cursor-default"
                  variants={{
                    hidden: { opacity: 0, y: 50, rotateX: -90, filter: "blur(8px)" },
                    visible: { opacity: 1, y: 0, rotateX: 0, filter: "blur(0px)" },
                  }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -10, color: "hsl(var(--primary))", scale: 1.1, transition: { duration: 0.2 } }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.h1>
          </motion.div>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground/80 mt-4 sm:mt-6 leading-snug"
          >
            {t('hero.tagline_main')}{" "}
            <span className="text-primary relative">
              {t('hero.tagline_highlight')}
              <motion.span
                className="absolute -bottom-1 left-0 right-0 h-[3px] bg-primary/30 rounded-full"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1.2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                style={{ originX: 0 }}
              />
            </span>
          </motion.p>

          {/* Value prop */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="text-sm sm:text-base text-muted-foreground mt-4 max-w-xl mx-auto lg:mx-0 leading-relaxed"
          >
            {t('hero.value_prop')}
          </motion.p>

          {/* Entity definition — hidden, for LLM/crawler extraction only */}
          <p className="sr-only">
            {t('hero.entity_definition')}
          </p>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            className="mt-7 sm:mt-9 flex flex-col sm:flex-row items-center lg:items-start gap-4"
          >
            <motion.button
              onClick={() => navigate(lp("/auth"))}
              className="relative group rounded-[20px] font-bold text-primary-foreground overflow-hidden text-base sm:text-lg h-[64px] px-16"
              style={{
                background: "linear-gradient(135deg, hsl(var(--primary)), hsl(25 90% 55%), hsl(var(--accent)))",
                boxShadow: "0 8px 32px hsl(var(--primary) / 0.35), 0 2px 8px hsl(var(--primary) / 0.2), inset 0 1px 0 hsla(0,0%,100%,0.2)",
              }}
              whileHover={{ scale: 1.06, y: -4, boxShadow: "0 16px 48px hsl(var(--primary) / 0.4), 0 4px 12px hsl(var(--primary) / 0.25), inset 0 1px 0 hsla(0,0%,100%,0.25)" }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
            >
              {/* Shimmer sweep */}
              <motion.div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: "linear-gradient(105deg, transparent 30%, hsla(0,0%,100%,0.25) 48%, hsla(0,0%,100%,0.35) 50%, hsla(0,0%,100%,0.25) 52%, transparent 70%)",
                  backgroundSize: "250% 100%",
                }}
                animate={{ backgroundPosition: ["-100% 0", "250% 0"] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 0.5 }}
              />
              {/* Inner top highlight */}
              <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              <span className="relative z-10 flex items-center gap-2.5">
                {t('hero.cta_primary')}
                <motion.span animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}>→</motion.span>
              </span>
            </motion.button>

            <motion.button
              onClick={scrollToHowItWorks}
              className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 py-2.5 px-4 font-medium rounded-xl hover:bg-primary/5"
              whileHover={{ scale: 1.02 }}
            >
              {t('hero.cta_secondary')}
            </motion.button>
          </motion.div>

          {/* Trust line */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.7, duration: 0.6 }}
            className="mt-5 flex items-center gap-4 justify-center lg:justify-start text-xs text-muted-foreground/50 font-medium"
          >
            {[
              { icon: Shield, label: "Free forever" },
              { icon: Zap, label: "No credit card" },
              { icon: Heart, label: "Setup in 2 min" },
            ].map((item, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <item.icon size={12} className="text-primary/40" />
                {item.label}
              </span>
            ))}
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 0.8 }}
            className="mt-8 sm:mt-10 flex items-center gap-6 sm:gap-10 justify-center lg:justify-start"
          >
            {[
              { icon: <PawPrint className="w-4 h-4 text-primary/60" />, value: 2400, suffix: "+", label: t('hero.stats.pets_label'), delay: 2200 },
              { icon: <Star className="w-4 h-4 text-primary/60" />, value: 98, suffix: "%", label: t('hero.stats.happy_label'), delay: 2400 },
              { icon: <MessageCircle className="w-4 h-4 text-primary/60" />, value: 500, suffix: "K", label: t('hero.stats.messages_label'), delay: 2600 },
            ].map((stat, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="w-px h-10 bg-border/40" />}
                <div className="flex flex-col items-center gap-1">
                  <div className="flex items-center gap-1.5">
                    {stat.icon}
                    <span className="text-xl sm:text-2xl font-black text-foreground/80 tabular-nums">
                      <AnimatedCounter target={stat.value} suffix={stat.suffix} delay={stat.delay} />
                    </span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-muted-foreground/50 font-medium">{stat.label}</span>
                </div>
              </React.Fragment>
            ))}
          </motion.div>
        </motion.div>

        {/* Right: Dog illustration */}
        <motion.div className="flex-1 flex justify-center relative" style={{ y: imageY }}>
          {/* Rotating conic ring */}
          <motion.div
            className="absolute w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] lg:w-[420px] lg:h-[420px] rounded-full"
            style={{
              background: "conic-gradient(from 0deg, hsl(var(--primary) / 0.15), transparent 25%, hsl(var(--accent) / 0.1), transparent 50%, hsl(var(--primary) / 0.12), transparent 75%, hsl(var(--accent) / 0.08))",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          />
          {/* Inner glow */}
          <div
            className="absolute w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] lg:w-[360px] lg:h-[360px] rounded-full"
            style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.1) 0%, transparent 70%)" }}
          />
          {/* Pulse ring */}
          <motion.div
            className="absolute w-[240px] h-[240px] sm:w-[300px] sm:h-[300px] lg:w-[380px] lg:h-[380px] rounded-full border border-primary/10"
            animate={{ scale: [1, 1.08, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />

          <div className="hidden sm:contents">
            <FloatingChatBubble text={t('hero_bubbles.bubble_1')} side="left" delay={1.8} x="-12%" y="12%" rotate={-3} />
            <FloatingChatBubble text={t('hero_bubbles.bubble_2')} side="right" delay={2.3} x="62%" y="2%" rotate={2} />
            <FloatingChatBubble text={t('hero_bubbles.bubble_3')} side="left" delay={2.8} x="58%" y="72%" rotate={1} />
          </div>

          <motion.img
            src={heroDog}
            alt={t('hero.dog_alt')}
            className="relative z-10 w-[200px] sm:w-[260px] md:w-[300px] lg:w-[400px] object-contain"
            loading="eager"
            decoding="async"
            initial={{ opacity: 0, scale: 0.8, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            style={{ filter: "drop-shadow(0 24px 48px hsl(20 60% 40% / 0.2))" }}
          />
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        style={{ opacity: fadeOut }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-1.5 text-muted-foreground/40"
        >
          <span className="text-[9px] uppercase tracking-[0.2em] font-semibold">Scroll</span>
          <ArrowDown size={14} />
        </motion.div>
      </motion.div>
    </section>
  );
};
